import React, { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  VALIDATE_WORK_ORDERS_FOR_INVOICING,
  CREATE_QUICKBOOKS_INVOICES,
} from '../../graphql/quickbooks';

const GROUPING_STRATEGIES = [
  { value: 'PER_WORK_ORDER', label: 'One estimate per work order', description: 'Create a separate estimate for each work order' },
  { value: 'BY_CUSTOMER', label: 'Group by customer', description: 'Combine all work orders for the same customer into one estimate' },
  { value: 'BY_PROPERTY', label: 'Group by property', description: 'Combine all work orders for the same property into one estimate' },
];

const LINE_ITEM_FORMATS = [
  { value: 'DETAILED', label: 'Detailed', description: 'One line per billable item (expenses, labor entries)' },
  { value: 'SUMMARIZED', label: 'Summarized', description: 'One line per work order with total amount' },
  { value: 'CATEGORIZED', label: 'Categorized', description: 'Separate lines for expenses, labor, and service charges' },
];

const QuickBooksInvoiceModal = ({ 
  isOpen, 
  onClose, 
  workOrders = [],
  onInvoicesCreated 
}) => {
  const [groupingStrategy, setGroupingStrategy] = useState('BY_PROPERTY');
  const [lineItemFormat, setLineItemFormat] = useState('DETAILED');
  const [isCreating, setIsCreating] = useState(false);

  // Get work order IDs from the passed work orders
  const workOrderIds = useMemo(() => workOrders.map(wo => wo.id), [workOrders]);

  // Validate work orders
  const { data: validationData, loading: validating, refetch: revalidate } = useQuery(
    VALIDATE_WORK_ORDERS_FOR_INVOICING,
    {
      variables: { workOrderIds },
      skip: !isOpen || workOrderIds.length === 0,
      fetchPolicy: 'network-only',
    }
  );

  // Revalidate when modal opens
  useEffect(() => {
    if (isOpen && workOrderIds.length > 0) {
      revalidate();
    }
  }, [isOpen, workOrderIds, revalidate]);

  const validation = validationData?.validateWorkOrdersForInvoicing;

  // Create invoices mutation
  const [createInvoices] = useMutation(CREATE_QUICKBOOKS_INVOICES);

  const handleCreateInvoices = async () => {
    if (!validation?.canCreateInvoices) return;

    setIsCreating(true);
    try {
      const { data } = await createInvoices({
        variables: {
          input: {
            workOrderIds,
            groupingStrategy,
            lineItemFormat,
          },
        },
      });

      const result = data?.createQuickBooksInvoices;
      if (result?.success) {
        toast.success(`Successfully created ${result.totalInvoicesCreated} estimate(s) totaling $${result.totalAmountInvoiced.toFixed(2)}`);

        // Call callback to refresh data
        if (onInvoicesCreated) {
          onInvoicesCreated(result);
        }

        onClose();
      } else {
        const errorMsg = result?.errors?.[0]?.message || 'Failed to create estimates';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error creating estimates:', error);
      toast.error(error.message || 'An error occurred while creating estimates');
    } finally {
      setIsCreating(false);
    }
  };

  // Calculate billable total from work orders
  const totalBillable = useMemo(() => {
    return workOrders.reduce((sum, wo) => {
      return sum + (wo.totalBilledAmount || 0);
    }, 0);
  }, [workOrders]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <svg className="h-5 w-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Send to QuickBooks
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close modal"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClose()}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-700">{workOrders.length}</div>
              <div className="text-sm text-blue-600">Work Orders</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-700">
                ${totalBillable.toFixed(2)}
              </div>
              <div className="text-sm text-green-600">Estimated Total</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-700">
                {validation?.validWorkOrderCount ?? '—'}
              </div>
              <div className="text-sm text-purple-600">Valid for Estimates</div>
            </div>
          </div>

          {/* Validation Messages */}
          {validating ? (
            <div className="flex items-center justify-center py-4">
              <svg className="animate-spin h-6 w-6 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Validating work orders...
            </div>
          ) : validation ? (
            <div className="space-y-3">
              {/* Missing Customer Warning */}
              {validation.missingCustomerCount > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-start">
                  <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <strong>{validation.missingCustomerCount} work order(s)</strong> cannot be sent to QuickBooks because their 
                    properties do not have a QuickBooks customer assigned.
                  </div>
                </div>
              )}

              {/* Already Invoiced Warning */}
              {validation.alreadyInvoicedCount > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-md flex items-start">
                  <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <strong>{validation.alreadyInvoicedCount} work order(s)</strong> have already been sent to QuickBooks. 
                    Creating new estimates will result in duplicate entries.
                  </div>
                </div>
              )}

              {/* Issues List */}
              {validation.issues && validation.issues.length > 0 && (
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                  <div className="text-sm font-medium mb-2">Issues Found:</div>
                  {validation.issues.map((issue, idx) => (
                    <div key={idx} className="text-sm py-1 flex items-center gap-2">
                      <span 
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          issue.issueType === 'MISSING_CUSTOMER' 
                            ? 'bg-red-100 text-red-800' 
                            : issue.issueType === 'ALREADY_INVOICED'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {issue.issueType === 'MISSING_CUSTOMER' ? 'No Customer' : 
                         issue.issueType === 'ALREADY_INVOICED' ? 'Already Sent' : 'Issue'}
                      </span>
                      <span className="font-medium">{issue.propertyName}</span>
                      <span className="text-gray-500">- {issue.issue}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Can Create Message */}
              {validation.canCreateInvoices && (
                <div className="p-3 bg-green-50 border border-green-200 text-green-800 rounded-md flex items-start">
                  <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    Ready to create estimates for <strong>{validation.validWorkOrderCount}</strong> work orders 
                    (estimated total: <strong>${validation.estimatedTotal?.toFixed(2) || totalBillable.toFixed(2)}</strong>)
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Estimate Options */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estimate Grouping</label>
              <select 
                value={groupingStrategy} 
                onChange={(e) => setGroupingStrategy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
              >
                {GROUPING_STRATEGIES.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {GROUPING_STRATEGIES.find(s => s.value === groupingStrategy)?.description}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Line Item Format</label>
              <select 
                value={lineItemFormat} 
                onChange={(e) => setLineItemFormat(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
              >
                {LINE_ITEM_FORMATS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {LINE_ITEM_FORMATS.find(f => f.value === lineItemFormat)?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isCreating}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreateInvoices}
            disabled={isCreating || validating || !validation?.canCreateInvoices}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center"
          >
            {isCreating ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Estimates...
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Create Estimates
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickBooksInvoiceModal;
